import React, { useState, useCallback, useRef } from 'react';
import { Upload, ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { Modal, ModalContent } from '../shared/Modal';
import { Button } from '../shared/Button';
import { useTheme } from '../../contexts/ThemeContext';
import {
  parseCSV,
  autoMapColumns,
  autoMapInvestmentColumns,
  applyMapping,
  applyInvestmentMapping,
  findHeaderRow,
  detectMultiAccountCSV,
  RETIREMENT_401K_CSV_FIELDS,
  INVESTMENT_CSV_FIELDS,
  Mapped401kRow,
  MappedInvestmentRow,
  CSVFieldDefinition,
} from '../../utils/csvParser';
import { formatCurrency } from '../../utils/formatters';
import { INVESTMENT_ACCOUNT_TYPES } from '../../config/constants';

type Stage = 'upload' | 'map' | 'preview';
type ImportMode = '401k' | 'investment';

interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onImport: (rows: any[]) => void;
  mode?: ImportMode;
}

const MODE_CONFIG: Record<ImportMode, {
  title: string;
  fields: CSVFieldDefinition[];
  dropzoneTitle: string;
  dropzoneSubtext: string;
  itemLabel: string;
}> = {
  '401k': {
    title: 'Import 401k CSV',
    fields: RETIREMENT_401K_CSV_FIELDS,
    dropzoneTitle: 'Drop your 401k CSV here',
    dropzoneSubtext: 'or click to browse. Works with Fidelity, Schwab, Vanguard, Empower, and more.',
    itemLabel: 'Holdings',
  },
  investment: {
    title: 'Import Investment CSV',
    fields: INVESTMENT_CSV_FIELDS,
    dropzoneTitle: 'Drop your investment CSV here',
    dropzoneSubtext: 'or click to browse. Ticker symbols will get real-time pricing via GOOGLEFINANCE.',
    itemLabel: 'Investments',
  },
};

export const CSVImportModal: React.FC<CSVImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  mode = '401k',
}) => {
  const config = MODE_CONFIG[mode];
  const { darkMode } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [stage, setStage] = useState<Stage>('upload');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, number>>({});
  const [mappedData, setMappedData] = useState<(Mapped401kRow | MappedInvestmentRow)[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [defaultAccountType, setDefaultAccountType] = useState('Individual');

  const reset = useCallback(() => {
    setStage('upload');
    setCsvHeaders([]);
    setCsvRows([]);
    setMapping({});
    setMappedData([]);
    setError('');
    setDefaultAccountType('Individual');
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const processFile = useCallback((file: File) => {
    setError('');
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a .csv file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) {
        setError('Could not read file');
        return;
      }

      const allRows = parseCSV(text);
      if (allRows.length < 2) {
        setError('CSV must have a header row and at least one data row');
        return;
      }

      // For investment mode, check if this is a multi-account CSV (e.g. Schwab All Accounts)
      if (mode === 'investment') {
        const sections = detectMultiAccountCSV(allRows);
        if (sections) {
          // Auto-map and combine all sections
          const combined: MappedInvestmentRow[] = [];
          for (const section of sections) {
            const sectionMapping = autoMapInvestmentColumns(section.headerRow);
            const sectionRows = applyInvestmentMapping(section.dataRows, sectionMapping, section.accountLabel);
            combined.push(...sectionRows);
          }
          if (combined.length === 0) {
            setError('No valid investment rows found in the multi-account CSV');
            return;
          }
          setMappedData(combined);
          setStage('preview');
          return;
        }
      }

      const headerIdx = findHeaderRow(allRows);
      const headers = allRows[headerIdx];
      const dataRows = allRows.slice(headerIdx + 1);

      setCsvHeaders(headers);
      setCsvRows(dataRows);

      // Auto-map columns based on mode
      const autoMap = mode === 'investment' ? autoMapInvestmentColumns(headers) : autoMapColumns(headers);
      setMapping(autoMap);
      setStage('map');
    };
    reader.readAsText(file);
  }, [mode]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleMappingChange = useCallback(
    (fieldKey: string, colIndex: number | undefined) => {
      setMapping((prev) => {
        const next = { ...prev };
        if (colIndex === undefined) {
          delete next[fieldKey];
        } else {
          next[fieldKey] = colIndex;
        }
        return next;
      });
    },
    []
  );

  const requiredFieldsMapped = config.fields
    .filter((f) => f.required)
    .every((f) => mapping[f.key] !== undefined);

  const accountColumnSkipped = mode === 'investment' && mapping['account'] === undefined;

  const handlePreview = useCallback(() => {
    let data: (Mapped401kRow | MappedInvestmentRow)[];
    if (mode === 'investment') {
      data = applyInvestmentMapping(csvRows, mapping, accountColumnSkipped ? defaultAccountType : undefined);
    } else {
      data = applyMapping(csvRows, mapping);
    }
    setMappedData(data);
    setStage('preview');
  }, [csvRows, mapping, mode, accountColumnSkipped, defaultAccountType]);

  const handleImport = useCallback(() => {
    onImport(mappedData);
    handleClose();
  }, [mappedData, onImport, handleClose]);

  const totalValue = mappedData.reduce((sum, r) => {
    if ('balance' in r) return sum + r.balance;
    if ('costBasis' in r) return sum + (r.shares * r.costBasis);
    return sum;
  }, 0);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="lg">
      <ModalContent>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            {config.title}
          </h3>
          <button
            onClick={handleClose}
            className={`p-1 rounded-lg ${darkMode ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Stage indicator */}
        <div className="flex items-center gap-2 mb-6">
          {(['upload', 'map', 'preview'] as Stage[]).map((s, i) => (
            <React.Fragment key={s}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  stage === s
                    ? 'bg-violet-600 text-white'
                    : i < ['upload', 'map', 'preview'].indexOf(stage)
                    ? darkMode
                      ? 'bg-green-600 text-white'
                      : 'bg-green-500 text-white'
                    : darkMode
                    ? 'bg-slate-700 text-slate-400'
                    : 'bg-slate-200 text-slate-500'
                }`}
              >
                {i < ['upload', 'map', 'preview'].indexOf(stage) ? (
                  <Check size={14} />
                ) : (
                  i + 1
                )}
              </div>
              {i < 2 && (
                <div
                  className={`flex-1 h-0.5 ${
                    i < ['upload', 'map', 'preview'].indexOf(stage)
                      ? 'bg-green-500'
                      : darkMode
                      ? 'bg-slate-700'
                      : 'bg-slate-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Upload Stage */}
        {stage === 'upload' && (
          <div>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                dragActive
                  ? 'border-violet-500 bg-violet-500/10'
                  : darkMode
                  ? 'border-slate-600 hover:border-violet-500 hover:bg-violet-500/5'
                  : 'border-slate-200 hover:border-violet-400 hover:bg-violet-50'
              }`}
            >
              <Upload
                size={48}
                className={`mx-auto mb-4 ${
                  dragActive
                    ? 'text-violet-400'
                    : darkMode
                    ? 'text-slate-500'
                    : 'text-slate-400'
                }`}
              />
              <p className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {config.dropzoneTitle}
              </p>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {config.dropzoneSubtext}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
            {error && (
              <p className="text-red-500 text-sm mt-3 text-center">{error}</p>
            )}
          </div>
        )}

        {/* Map Stage */}
        {stage === 'map' && (
          <div>
            <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Map your CSV columns to {mode === 'investment' ? 'investment' : '401k'} fields. We auto-detected what we could.
            </p>

            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
              {config.fields.map((field) => (
                <div key={field.key} className="flex items-center gap-3">
                  <div className={`w-40 text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </div>
                  <select
                    value={mapping[field.key] !== undefined ? mapping[field.key] : ''}
                    onChange={(e) =>
                      handleMappingChange(
                        field.key,
                        e.target.value === '' ? undefined : parseInt(e.target.value)
                      )
                    }
                    className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                      darkMode
                        ? 'bg-slate-800 border-slate-600 text-white'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  >
                    <option value="">-- Skip --</option>
                    {csvHeaders.map((header, idx) => (
                      <option key={idx} value={idx}>
                        {header}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            {accountColumnSkipped && (
              <div className={`flex items-center gap-3 mt-3 pt-3 border-t ${darkMode ? 'border-white/10' : 'border-slate-200'}`}>
                <div className={`w-40 text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Default Account Type
                </div>
                <div className="flex-1 flex gap-2">
                  <select
                    value={INVESTMENT_ACCOUNT_TYPES.includes(defaultAccountType) ? defaultAccountType : '__custom__'}
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        setDefaultAccountType('');
                      } else {
                        setDefaultAccountType(e.target.value);
                      }
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                      darkMode
                        ? 'bg-slate-800 border-slate-600 text-white'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  >
                    {INVESTMENT_ACCOUNT_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                    <option value="__custom__">Custom...</option>
                  </select>
                  {!INVESTMENT_ACCOUNT_TYPES.includes(defaultAccountType) && (
                    <input
                      type="text"
                      value={defaultAccountType}
                      onChange={(e) => setDefaultAccountType(e.target.value)}
                      placeholder="e.g., My Brokerage Account"
                      autoFocus
                      className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                        darkMode
                          ? 'bg-slate-800 border-slate-600 text-white'
                          : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    />
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <Button variant="secondary" onClick={() => setStage('upload')}>
                <ArrowLeft size={16} className="mr-1" />
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handlePreview}
                disabled={!requiredFieldsMapped}
                fullWidth
              >
                Preview
                <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Preview Stage */}
        {stage === 'preview' && (
          <div>
            <p className={`text-sm mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              {mappedData.length} {config.itemLabel.toLowerCase()} found totaling{' '}
              <span className="font-bold">{formatCurrency(totalValue)}</span>
            </p>

            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr
                    className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} border-b ${
                      darkMode ? 'border-white/10' : 'border-slate-200'
                    }`}
                  >
                    {mode === 'investment' ? (
                      <>
                        <th className="text-left pb-2 pr-3">Symbol</th>
                        <th className="text-right pb-2 pr-3">Shares</th>
                        <th className="text-right pb-2 pr-3">Cost Basis</th>
                        <th className="text-left pb-2">Account</th>
                      </>
                    ) : (
                      <>
                        <th className="text-left pb-2 pr-3">Fund</th>
                        <th className="text-left pb-2 pr-3">Ticker</th>
                        <th className="text-right pb-2 pr-3">Shares</th>
                        <th className="text-right pb-2">Balance</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {mappedData.slice(0, 10).map((row, idx) => (
                    <tr
                      key={idx}
                      className={`${darkMode ? 'text-slate-300' : 'text-slate-800'} border-b ${
                        darkMode ? 'border-white/10' : 'border-slate-200'
                      }`}
                    >
                      {mode === 'investment' && 'symbol' in row ? (
                        <>
                          <td className="py-1.5 pr-3 font-mono font-semibold">{row.symbol}</td>
                          <td className="py-1.5 pr-3 text-right">{row.shares.toFixed(2)}</td>
                          <td className="py-1.5 pr-3 text-right font-semibold">
                            {formatCurrency(row.costBasis)}
                          </td>
                          <td className="py-1.5 text-xs">{row.account}</td>
                        </>
                      ) : 'fundName' in row ? (
                        <>
                          <td className="py-1.5 pr-3 max-w-[180px] truncate">{row.fundName}</td>
                          <td className="py-1.5 pr-3 font-mono">{row.ticker || '-'}</td>
                          <td className="py-1.5 pr-3 text-right">{row.shares.toFixed(3)}</td>
                          <td className="py-1.5 text-right font-semibold">
                            {formatCurrency(row.balance)}
                          </td>
                        </>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
              {mappedData.length > 10 && (
                <p className={`text-xs mt-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  + {mappedData.length - 10} more rows
                </p>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="secondary" onClick={() => setStage('map')}>
                <ArrowLeft size={16} className="mr-1" />
                Back
              </Button>
              <Button variant="primary" onClick={handleImport} fullWidth>
                Import {mappedData.length} {config.itemLabel}
              </Button>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};
